import { History, Location } from 'history';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ListControls } from '../types/list';
import { debounce } from '../util';
import { toQueryString } from '../util/listUrl';

interface UseSyncedSearchArgs {
  storeSearchValue: string;
  setStoreSearch: (value: string) => void;
  pageSize: number;
  sort: string;
  history: History;
  location: Location;
  onSearch: (value: string) => void | Promise<void>;
}

interface UseSyncedSearchResult {
  searchValue: string;
  onChange: (value: string) => void;
  clear: () => void;
}

export function useSyncedSearch({
  storeSearchValue,
  setStoreSearch,
  pageSize,
  sort,
  history,
  location,
  onSearch,
}: UseSyncedSearchArgs): UseSyncedSearchResult {
  const [searchValue, setSearchValue] = useState<string>(
    storeSearchValue ?? ''
  );

  // Keep local input synced with store value (handles Back/Forward cases)
  useEffect(() => {
    if (storeSearchValue !== searchValue) {
      setSearchValue(storeSearchValue ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSearchValue]);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => void onSearch(value), 500),
    [onSearch]
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const updateUrl = useCallback(
    (value: string) => {
      const queryString = toQueryString({
        page: 1,
        pageSize: pageSize,
        search: value,
        sort,
      } as ListControls);

      history.replace({
        pathname: location.pathname,
        search: `?${queryString}`,
      });
    },
    [history, location.pathname, pageSize, sort]
  );

  const onChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      setStoreSearch(value);
      updateUrl(value);
      debouncedSearch(value);
    },
    [debouncedSearch, setStoreSearch, updateUrl]
  );

  const clear = useCallback(() => onChange(''), [onChange]);

  return { searchValue, onChange, clear };
}
